import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../../context/AuthContext";
import { siteApi } from "../../../lib/api";
import { launchGameById, launchProviderLobby } from "../../../lib/gameLaunch";
import FavoriteToggleButton from "../../home/MainHome/FavoriteToggleButton";
import {
  createFavoriteGameItem,
  createFavoriteProviderItem,
  FAVORITES_UPDATED_EVENT,
  getFavoriteGames,
  toggleFavoriteGame,
} from "../../../utils/favoriteGames";
import {
  dedupeGames,
  formatCount,
  getGameImage,
  getGameTitle,
  getRows,
  getVendorImages,
  getVendorTitle,
  matchesKeyword,
} from "../../../utils/gameCatalog";
import { getRecentPlayAccountKey } from "../../../utils/recentPlayHistory";
import { StyledGameCatalogPage } from "./StyledGameCatalogPage";

const PAGE_SIZE = 40;

const SORT_OPTIONS = [
  { value: "popular", label: "인기", orderByName: "sequence", orderByType: "ASC" },
  { value: "name", label: "이름순", orderByName: "game_name", orderByType: "ASC" },
];

const TABS = [
  { value: "all", label: "전체" },
  { value: "casino", label: "카지노" },
  { value: "slots", label: "슬롯" },
];

const getTabFromSearch = (search) => {
  const tab = new URLSearchParams(search).get("tab");
  return TABS.some((item) => item.value === tab) ? tab : "all";
};

const getVendorFromSearch = (search) => new URLSearchParams(search).get("vendor") || "";

const getSortConfig = (sortBy) =>
  SORT_OPTIONS.find((option) => option.value === sortBy) || SORT_OPTIONS[0];

const dispatchAuthRequired = () => {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("crypto-auth-required", {
      detail: { modalOption: "login", reason: "favorite" },
    })
  );
};

const CatalogImageCard = ({
  title,
  image,
  meta,
  isFavorite = false,
  isLaunching = false,
  onFavoriteToggle,
  onClick,
  to,
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const content = (
    <>
      <span className="catalog-card-image">
        {image && !imageFailed ? (
          <img src={image} alt={title} loading="lazy" onError={() => setImageFailed(true)} />
        ) : (
          <span className="catalog-fallback-title">{title}</span>
        )}
        <span className="catalog-card-title">{title}</span>
        <span className="catalog-card-overlay">
          <span className="catalog-play-button" aria-hidden="true" />
          <strong>{isLaunching ? "실행 중" : title}</strong>
        </span>
      </span>
      <span className="catalog-card-meta">
        <span className="live-dot" />
        {meta}
      </span>
    </>
  );

  if (to) {
    return (
      <div className="catalog-card-shell">
        <a className="catalog-card-link" href={to}>
          {content}
        </a>
        <FavoriteToggleButton active={isFavorite} label={title} onToggle={onFavoriteToggle} />
      </div>
    );
  }

  return (
    <div className="catalog-card-shell">
      <button type="button" className="catalog-card-button" onClick={onClick}>
        {content}
      </button>
      <FavoriteToggleButton active={isFavorite} label={title} onToggle={onFavoriteToggle} />
    </div>
  );
};

const CasinoGamesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const loadMoreRef = useRef(null);
  const requestRef = useRef(0);
  const loadingMoreRef = useRef(false);

  const [tab, setTab] = useState(() => getTabFromSearch(location.search));
  const [selectedVendor, setSelectedVendor] = useState(() => getVendorFromSearch(location.search));
  const [casinoVendors, setCasinoVendors] = useState([]);
  const [slotVendors, setSlotVendors] = useState([]);
  const [slotGames, setSlotGames] = useState([]);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("popular");
  const [vendorLoading, setVendorLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState("");
  const [launchingId, setLaunchingId] = useState("");
  const [favoriteKeys, setFavoriteKeys] = useState(() => new Set());
  const accountKey = useMemo(() => getRecentPlayAccountKey(user), [user]);

  useEffect(() => {
    setTab(getTabFromSearch(location.search));
    setSelectedVendor(getVendorFromSearch(location.search));
  }, [location.search]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedSearch(search.trim()), 280);
    return () => window.clearTimeout(timeoutId);
  }, [search]);

  useEffect(() => {
    let isMounted = true;

    const loadVendors = async () => {
      setVendorLoading(true);
      try {
        const [casinoResponse, slotResponse] = await Promise.all([
          siteApi.getGameVendors({
            game_type: "live-casino",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          }),
          siteApi.getGameVendors({
            game_type: "slots",
            order_by_name: "sequence",
            order_by_type: "ASC",
            limit: "ALL",
          }),
        ]);

        if (!isMounted) return;
        setCasinoVendors(casinoResponse.success ? getRows(casinoResponse) : []);
        setSlotVendors(slotResponse.success ? getRows(slotResponse) : []);
      } finally {
        if (isMounted) setVendorLoading(false);
      }
    };

    loadVendors();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!accountKey) {
      setFavoriteKeys(new Set());
      return undefined;
    }

    const refreshFavorites = () => {
      setFavoriteKeys(new Set(getFavoriteGames(accountKey).map((item) => item.key)));
    };

    refreshFavorites();

    const handleFavoritesUpdated = (event) => {
      if (!event?.detail?.accountKey || event.detail.accountKey === accountKey) {
        refreshFavorites();
      }
    };

    const handleStorage = (event) => {
      if (!event.key || event.key.endsWith(`:${accountKey}`)) {
        refreshFavorites();
      }
    };

    window.addEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
    window.addEventListener("storage", handleStorage);

    return () => {
      window.removeEventListener(FAVORITES_UPDATED_EVENT, handleFavoritesUpdated);
      window.removeEventListener("storage", handleStorage);
    };
  }, [accountKey]);

  const shouldLoadSlots = tab !== "casino";

  const loadSlotGames = useCallback(
    async ({ pageToLoad = 1, append = false } = {}) => {
      if (!shouldLoadSlots) {
        setSlotGames([]);
        setIsInitialLoading(false);
        return;
      }

      if (append && loadingMoreRef.current) return;

      const requestId = ++requestRef.current;
      const sortConfig = getSortConfig(sortBy);

      if (append) {
        loadingMoreRef.current = true;
        setIsLoadingMore(true);
      } else {
        setIsInitialLoading(true);
        setError("");
      }

      try {
        const params = {
          page: pageToLoad,
          limit: PAGE_SIZE,
          game_type: "slots",
          order_by_name: sortConfig.orderByName,
          order_by_type: sortConfig.orderByType,
        };

        if (selectedVendor) params.vendor = selectedVendor;
        if (debouncedSearch) params.search = debouncedSearch;

        const response = await siteApi.getGames(params);
        if (requestId !== requestRef.current) return;

        if (!response.success) {
          if (!append) setSlotGames([]);
          setError(response.error || "슬롯 게임을 불러오지 못했습니다.");
          return;
        }

        const rows = getRows(response);
        setSlotGames((current) => (append ? dedupeGames([...current, ...rows]) : rows));
        setPage(pageToLoad);
        setNextPage(response.data?.nextPage || null);
        setTotal(Number(response.data?.count || 0));
      } catch {
        if (requestId !== requestRef.current) return;
        if (!append) setSlotGames([]);
        setError("슬롯 게임을 불러오지 못했습니다.");
      } finally {
        if (requestId === requestRef.current || append) {
          if (append) {
            loadingMoreRef.current = false;
            setIsLoadingMore(false);
          } else {
            setIsInitialLoading(false);
          }
        }
      }
    },
    [debouncedSearch, selectedVendor, shouldLoadSlots, sortBy]
  );

  useEffect(() => {
    setSlotGames([]);
    setPage(1);
    setNextPage(null);
    setTotal(0);
    loadSlotGames({ pageToLoad: 1, append: false });
  }, [loadSlotGames]);

  const casinoRows = useMemo(() => {
    const rows = casinoVendors.filter((vendor) =>
      matchesKeyword(
        debouncedSearch,
        getVendorTitle(vendor),
        vendor?.vendor_name,
        vendor?.display_name
      )
    );

    if (sortBy === "name") {
      return [...rows].sort((a, b) => getVendorTitle(a).localeCompare(getVendorTitle(b), "ko"));
    }

    return rows;
  }, [casinoVendors, debouncedSearch, sortBy]);

  const vendorTitleByName = useMemo(() => {
    const map = new Map();
    slotVendors.forEach((vendor) => {
      if (vendor?.vendor_name) map.set(vendor.vendor_name, getVendorTitle(vendor));
    });
    return map;
  }, [slotVendors]);

  const visibleCasino = tab !== "slots";
  const visibleSlots = tab !== "casino";
  const hasMore = visibleSlots && (Boolean(nextPage) || (total > 0 && slotGames.length < total));

  const updateUrl = (nextTab, nextVendor = selectedVendor) => {
    const params = new URLSearchParams(location.search);
    if (nextTab && nextTab !== "all") params.set("tab", nextTab);
    else params.delete("tab");
    if (nextVendor) params.set("vendor", nextVendor);
    else params.delete("vendor");
    const nextSearch = params.toString();
    navigate(`/casino-games${nextSearch ? `?${nextSearch}` : ""}`);
  };

  const handleLoadMore = useCallback(() => {
    if (!hasMore || isInitialLoading || isLoadingMore) return;
    loadSlotGames({ pageToLoad: nextPage || page + 1, append: true });
  }, [hasMore, isInitialLoading, isLoadingMore, loadSlotGames, nextPage, page]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore || isInitialLoading) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) handleLoadMore();
      },
      { rootMargin: "520px 0px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, isInitialLoading]);

  const handleCasinoLaunch = async (vendor) => {
    const key = `casino:${vendor?.vendor_name || vendor?.id || ""}`;
    setLaunchingId(key);
    try {
      await launchProviderLobby(vendor, {
        label: getVendorTitle(vendor),
        gameType: "live-casino",
        meta: "카지노게임",
      });
    } finally {
      setLaunchingId("");
    }
  };

  const handleSlotLaunch = async (game) => {
    const key = `slot:${game?.game_id || game?.id || ""}:${game?.vendor || ""}`;
    setLaunchingId(key);
    try {
      await launchGameById(game?.game_id, {
        label: getGameTitle(game),
        recentPlay: {
          kind: "slot-game",
          gameType: "slots",
          gameId: game?.game_id || game?.id || "",
          vendorName: game?.vendor || game?.vendor_name || "",
          title: getGameTitle(game),
          image: getGameImage(game),
          meta: vendorTitleByName.get(game?.vendor) || "슬롯게임",
        },
      });
    } finally {
      setLaunchingId("");
    }
  };

  const handleFavoriteToggle = (item) => {
    if (!user?.isAuthenticated || !accountKey) {
      dispatchAuthRequired();
      return;
    }

    const result = toggleFavoriteGame(accountKey, item);
    setFavoriteKeys(new Set(result.rows.map((row) => row.key)));
  };

  return (
    <StyledGameCatalogPage>
      <div className="catalog-shell">
        <label className="catalog-search-box">
          <span className="catalog-search-icon" aria-hidden="true" />
          <input
            type="search"
            value={search}
            placeholder="카지노, 슬롯 게임 검색"
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>

        <div className="catalog-toolbar">
          <div className="catalog-tabs" role="tablist" aria-label="카지노 게임 분류">
            {TABS.map((item) => (
              <button
                type="button"
                key={item.value}
                className={`catalog-tab${tab === item.value ? " is-active" : ""}`}
                onClick={() => updateUrl(item.value, item.value === "casino" ? "" : selectedVendor)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <label className="catalog-sort-control">
            <span>정렬</span>
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value)}>
              {SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {visibleSlots ? (
          <div className="catalog-provider-strip" aria-busy={vendorLoading}>
            <button
              type="button"
              className={`catalog-provider-chip${!selectedVendor ? " is-active" : ""}`}
              onClick={() => updateUrl(tab, "")}
            >
              전체 슬롯
            </button>
            {vendorLoading
              ? Array.from({ length: 7 }).map((_, index) => (
                  <span className="catalog-provider-chip" key={`provider-skeleton-${index}`}>
                    ...
                  </span>
                ))
              : slotVendors.map((vendor) => (
                  <button
                    type="button"
                    key={vendor?.id || vendor?.vendor_name}
                    className={`catalog-provider-chip${
                      selectedVendor === vendor?.vendor_name ? " is-active" : ""
                    }`}
                    onClick={() => updateUrl(tab === "casino" ? "slots" : tab, vendor?.vendor_name || "")}
                  >
                    {getVendorTitle(vendor)}
                  </button>
                ))}
          </div>
        ) : null}

        {visibleCasino ? (
          <section className="catalog-section">
            <div className="catalog-section-head">
              <div className="catalog-section-title">
                <h1>카지노게임</h1>
                <span>{vendorLoading ? "불러오는 중" : `${casinoRows.length}개 게임사`}</span>
              </div>
            </div>

            <div className="catalog-grid" aria-busy={vendorLoading}>
              {vendorLoading
                ? Array.from({ length: 8 }).map((_, index) => (
                    <span className="catalog-skeleton" key={`casino-skeleton-${index}`} />
                  ))
                : casinoRows.map((vendor) => {
                    const title = getVendorTitle(vendor, "카지노게임");
                    const key = `casino:${vendor?.vendor_name || vendor?.id || ""}`;
                    const favoriteItem = createFavoriteProviderItem(vendor, {
                      label: title,
                      gameType: "live-casino",
                      meta: "카지노게임",
                      kind: "provider",
                      image: getVendorImages(vendor)[0] || "",
                    });
                    return (
                      <CatalogImageCard
                        key={key}
                        title={title}
                        image={getVendorImages(vendor)[0]}
                        meta="카지노 게임사"
                        isFavorite={favoriteKeys.has(favoriteItem.key)}
                        isLaunching={launchingId === key}
                        onFavoriteToggle={() => handleFavoriteToggle(favoriteItem)}
                        onClick={() => handleCasinoLaunch(vendor)}
                      />
                    );
                  })}
            </div>

            {!vendorLoading && casinoRows.length === 0 ? (
              <div className="catalog-state">표시할 카지노게임이 없습니다.</div>
            ) : null}
          </section>
        ) : null}

        {visibleSlots ? (
          <section className="catalog-section">
            <div className="catalog-section-head">
              <div className="catalog-section-title">
                <h2>슬롯게임</h2>
                <span>{total ? `${total.toLocaleString("ko-KR")}개 게임` : "실시간 슬롯 목록"}</span>
              </div>
            </div>

            {error ? <div className="catalog-state error">{error}</div> : null}

            <div className="catalog-grid" aria-busy={isInitialLoading}>
              {isInitialLoading
                ? Array.from({ length: 16 }).map((_, index) => (
                    <span className="catalog-skeleton" key={`slot-skeleton-${index}`} />
                  ))
                : slotGames.map((game) => {
                    const title = getGameTitle(game, "슬롯게임");
                    const key = `slot:${game?.game_id || game?.id || ""}:${game?.vendor || ""}`;
                    const playerCount =
                      formatCount(game?.playing_count) ||
                      formatCount(game?.online_count) ||
                      formatCount(game?.current_players);
                    const favoriteItem = createFavoriteGameItem(game, {
                      label: title,
                      gameType: "slots",
                      meta: vendorTitleByName.get(game?.vendor) || "슬롯게임",
                      image: getGameImage(game),
                    });
                    return (
                      <CatalogImageCard
                        key={key}
                        title={title}
                        image={getGameImage(game)}
                        meta={playerCount ? `${playerCount} 플레이중` : vendorTitleByName.get(game?.vendor) || "플레이 가능"}
                        isFavorite={favoriteKeys.has(favoriteItem.key)}
                        isLaunching={launchingId === key}
                        onFavoriteToggle={() => handleFavoriteToggle(favoriteItem)}
                        onClick={() => handleSlotLaunch(game)}
                      />
                    );
                  })}
            </div>

            {!isInitialLoading && !error && slotGames.length === 0 ? (
              <div className="catalog-state">표시할 슬롯 게임이 없습니다.</div>
            ) : null}

            <div className="catalog-load-more" ref={loadMoreRef}>
              {hasMore ? (
                <button type="button" onClick={handleLoadMore} disabled={isLoadingMore}>
                  {isLoadingMore ? "불러오는 중" : "더 불러오기"}
                </button>
              ) : slotGames.length > 0 ? (
                <span>모든 슬롯을 불러왔습니다.</span>
              ) : null}
            </div>
          </section>
        ) : null}
      </div>
    </StyledGameCatalogPage>
  );
};

export default CasinoGamesPage;
